import React, { useState, useMemo } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { LazyLoadImage } from "react-lazy-load-image-component";

import { useTranslation } from "components/i18n/source";

import ImagePlaceholder from "../components/Placeholders/ImagePlaceholder";

import styles from "./Image.module.scss";

/**
 * Image component should be wrapped with a container for sizing.
 * It is also lazy loaded by default
 */
function Image({
  src,
  alt,
  type,
  circular,
  contain,
  width,
  height,
  usePlaceholder,
  aspectRatio,
  lazyLoad,
  ...props
}) {
  const { t } = useTranslation();
  const imageClasses = classnames(
    styles.image,
    styles[type],
    contain && styles.contain,
    circular && styles.circular,
  );

  const [placeholderVisible, setPlaceholderVisible] = useState(true);

  const shouldRenderPlaceholder = useMemo(
    () => usePlaceholder && placeholderVisible,
    [usePlaceholder, placeholderVisible],
  );

  const style = {};
  if (width) {
    style.width = width;
  }
  if (height) {
    style.height = height;
  }

  const Placeholder = () =>
    shouldRenderPlaceholder ? (
      <ImagePlaceholder className={classnames(imageClasses, aspectRatio)} />
    ) : (
      ""
    );

  return (
    <LazyLoadImage
      {...props}
      style={style}
      className={classnames(
        imageClasses,
        shouldRenderPlaceholder && styles.hidden,
        aspectRatio,
        props.className || "",
      )}
      src={src}
      alt={t(alt)}
      onLoad={() => setPlaceholderVisible(false)}
      threshold={props?.threshold || 300}
      visibleByDefault={!lazyLoad}
      placeholder={<Placeholder />}
    />
  );
}

Image.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  type: PropTypes.oneOf(["opaque", "rounded", ""]),
  circular: PropTypes.bool,
  contain: PropTypes.bool,
  usePlaceholder: PropTypes.bool,
  aspectRatio: PropTypes.string,
  lazyLoad: PropTypes.bool,
};

Image.defaultProps = {
  type: "",
  width: "",
  height: "",
  circular: false,
  contain: false,
  usePlaceholder: false,
  aspectRatio: "",
  lazyLoad: true,
};

export default Image;
